import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Drag-to-reorder a table using SortableJS. Dragging starts from an explicit handle (so rows
// that also navigate on click via table-row-link keep working — table_row_link ignores clicks
// on <button>, and the handle is a button). On drop the new order is persisted with
// `PATCH <url-value>` as JSON `{ <param-value>: [id, id, ...] }` plus the CSRF token; the
// response body is ignored (fire-and-forget auto-save), but a non-OK status reloads to resync.
//
// forceFallback uses SortableJS's own pointer-tracked drag clone instead of the browser's
// native HTML5 drag image — the native image lags the cursor and the swap point trails far
// behind, which is exactly what we're fixing here. The clone is a <tr> lifted out of the
// table, so we pin each cell's width inline during the drag (freezeWidths) and re-establish a
// table box on it via CSS (.sortable-fallback-row) so the columns don't collapse.
//
//   <table data-controller="sortable-list"
//          data-sortable-list-url-value="/.../reorder"
//          data-sortable-list-param-value="ids">
//     <tbody>
//       <tr data-sortable-list-target="item" data-sortable-id="42">
//         <td><button type="button" data-sortable-list-target="handle">⠿</button></td>
//         ...
//       </tr>
//     </tbody>
//   </table>
export default class extends Controller {
  static targets = ["item", "handle"]
  static values = {
    url: String,
    param: { type: String, default: "ids" },
    // CSS class for the floating drag clone. Default suits table rows (re-boxed as a table);
    // non-table lists (e.g. the sidebar category list) pass their own class.
    fallbackClass: { type: String, default: "sortable-fallback-row" },
    // Append the floating clone to <body> instead of the list. The sidebar lives inside an
    // <aside> that is translateX-transformed on mobile, which would otherwise offset a
    // position:fixed clone; the sidebar list sets this true.
    fallbackOnBody: { type: Boolean, default: false },
  }

  connect() {
    // Swallow the one synthetic click the browser fires right after a forceFallback drag, so
    // dropping a row never also triggers its table-row-link navigation to the detail page.
    this._swallowClick = (e) => { e.stopPropagation(); e.preventDefault() }

    this.listEl = this.element.querySelector("tbody") || this.element

    // The same categories appear in several sortable containers that share ONE global order and
    // ONE reorder endpoint (the sidebar renders the list once per platform, plus the カテゴリマスタ
    // table). After any of them persists a new order we re-sequence the others, so a sibling
    // holding a stale snapshot can't later re-save and clobber the just-saved order.
    this._onPeerSaved = (e) => {
      if (this.hasUrlValue && e.detail && e.detail.url === this.urlValue) this.syncOrder(e.detail.ids)
    }
    document.addEventListener("sortable-list:saved", this._onPeerSaved)

    this.sortable = Sortable.create(this.listEl, {
      handle: "[data-sortable-list-target='handle']",
      draggable: "[data-sortable-list-target='item']",
      animation: 150,
      easing: "cubic-bezier(0.2, 0, 0, 1)",
      forceFallback: true,    // pointer-tracked clone (smooth, precise) instead of the laggy native drag image
      fallbackOnBody: this.fallbackOnBodyValue,
      fallbackTolerance: 4,   // a few px of travel before a handle press becomes a drag (keeps clicks crisp)
      ghostClass: "sortable-ghost-row",        // the gap left where the row will drop
      fallbackClass: this.fallbackClassValue,  // the floating clone that follows the cursor
      onChoose: (e) => this.freezeWidths(e.item),
      onUnchoose: (e) => this.unfreezeWidths(e.item),
      // Block reordering while a search filter is hiding rows: save() serializes ALL rows, so a
      // move now would bury the hidden rows in an order the user never saw.
      onMove: () => !this.hasHiddenRows(),
      // Always disarm a possible post-drag click; only persist when the order actually changed
      // (a no-op drop must not re-save a stale snapshot — see syncOrder).
      onEnd: (e) => { this.armClickSwallow(); if (e.oldIndex !== e.newIndex) this.save() },
    })
  }

  disconnect() {
    document.removeEventListener("sortable-list:saved", this._onPeerSaved)
    if (this._swallowTimer) clearTimeout(this._swallowTimer)
    this.element.removeEventListener("click", this._swallowClick, true)
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }

  // Re-sequence this container's item rows to match an authoritative id order broadcast by a
  // sibling that just saved. Pure DOM reorder (does not trigger a save). No-op if already in
  // order. Items are moved to the end of the list in order, which preserves any leading pinned
  // rows (e.g. locked メイン/アーカイブ on the カテゴリマスタ table).
  syncOrder(ids) {
    const current = this.itemTargets.map((el) => String(el.dataset.sortableId))
    const target = ids.map(String).filter((id) => current.includes(id))
    if (current.join(",") === target.join(",")) return
    const byId = new Map(this.itemTargets.map((el) => [String(el.dataset.sortableId), el]))
    target.forEach((id) => {
      const el = byId.get(id)
      if (el) this.listEl.appendChild(el)
    })
  }

  // Cancel the single click the browser fires immediately after a drag gesture (forceFallback
  // does not reliably suppress it once the row has actually moved), so a reorder never doubles
  // as a navigation. Armed for one click and auto-disarmed shortly after if none arrives.
  armClickSwallow() {
    this.element.addEventListener("click", this._swallowClick, { capture: true, once: true })
    if (this._swallowTimer) clearTimeout(this._swallowTimer)
    this._swallowTimer = setTimeout(() => {
      this.element.removeEventListener("click", this._swallowClick, true)
    }, 350)
  }

  hasHiddenRows() {
    return this.itemTargets.some((r) => r.style.display === "none")
  }

  // Pin each cell's current width inline so the lifted-out drag clone (a deep copy of the row)
  // keeps the table's column layout instead of collapsing. Restored on drag end / cancel.
  freezeWidths(row) {
    row.querySelectorAll("td").forEach((td) => {
      td.dataset.frozenWidth = td.style.width
      td.style.width = `${td.getBoundingClientRect().width}px`
    })
  }

  unfreezeWidths(row) {
    row.querySelectorAll("td").forEach((td) => {
      if (!("frozenWidth" in td.dataset)) return
      td.style.width = td.dataset.frozenWidth
      delete td.dataset.frozenWidth
    })
  }

  save() {
    if (!this.hasUrlValue || this.hasHiddenRows()) return
    const ids = this.itemTargets.map((row) => row.dataset.sortableId).filter(Boolean)
    const meta = document.querySelector("meta[name='csrf-token']")
    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": meta ? meta.content : "",
      },
      body: JSON.stringify({ [this.paramValue]: ids }),
    })
      .then((res) => {
        // A rejected save (expired session -> 401, CSRF mismatch -> 422, server error) would
        // otherwise leave the UI showing an order the server never stored. Reload to resync to
        // the true persisted order rather than fail silently.
        if (!res.ok) {
          console.error("[sortable-list] save rejected:", res.status)
          window.location.reload()
          return
        }
        // Tell sibling containers on the same endpoint to adopt the order we just persisted.
        document.dispatchEvent(new CustomEvent("sortable-list:saved", { detail: { url: this.urlValue, ids } }))
      })
      .catch((err) => {
        console.error("[sortable-list] save failed", err)
        window.location.reload()
      })
  }
}
